/**
* IBM Confidential
* Licensed Materials - Property of IBM
* (C) Copyright IBM Corp. 2020, 2024 All Rights Reserved.
* US Government Users Restricted Rights - Use, duplication or
* disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
* */

import { objectWithoutProperties as _objectWithoutProperties, slicedToArray as _slicedToArray, defineProperty as _defineProperty, objectSpread2 as _objectSpread2, extends as _extends } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Tag as Tag$2 } from '@carbon/react';
import { useTranslation } from '../../../Utilities/i18n/i18n.js';
import trackComponentEvent from '../../../Utilities/analytics/analytics.js';

var _excluded = ["children", "className", "type", "isRemovable", "maxCharacters", "onRemove", "onClick", "title"];
var TYPES = {
  functional: 'Functional',
  red: 'Red',
  magenta: 'Magenta',
  purple: 'Purple',
  blue: 'Blue',
  cyan: 'Cyan',
  teal: 'Teal',
  green: 'Green',
  gray: 'Gray',
  'cool-gray': 'Cool-Gray',
  'warm-gray': 'Warm-Gray'
};
var Tag = function Tag(_ref) {
  var _ref$children = _ref.children,
    children = _ref$children === void 0 ? '' : _ref$children,
    _ref$className = _ref.className,
    className = _ref$className === void 0 ? '' : _ref$className,
    _ref$type = _ref.type,
    type = _ref$type === void 0 ? 'functional' : _ref$type,
    isRemovable = _ref.isRemovable,
    _ref$maxCharacters = _ref.maxCharacters,
    maxCharacters = _ref$maxCharacters === void 0 ? 0 : _ref$maxCharacters,
    onRemove = _ref.onRemove,
    onClick = _ref.onClick,
    title = _ref.title,
    other = _objectWithoutProperties(_ref, _excluded);
  var _useTranslation = useTranslation(),
    t = _useTranslation.t;
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    removed = _useState2[0],
    setRemoved = _useState2[1];
  var tagClasses = classnames('cpc--tag', _defineProperty({
    'cpc--tag__removed': removed,
    'cpc--tag--removable': !!onRemove,
    'cpc--tag--clickable': onClick && !isRemovable
  }, className, className));
  var tagContentsClass = '';
  var carbonType = type === 'functional' ? 'blue' : type;
  var _getInnerTextLength = function getInnerTextLength(child) {
    if (child === null) {
      return 0;
    }
    if (typeof child === 'string') {
      return child.length;
    }
    return child.props && child.props.children ? _getInnerTextLength(child.props.children) : 0;
  };
  if (maxCharacters) {
    var childrenTextLength = 0;
    if (typeof children === 'string') {
      childrenTextLength = _getInnerTextLength(children);
    } else {
      children.forEach(function (child) {
        childrenTextLength += _getInnerTextLength(child);
      });
    }
    tagContentsClass = childrenTextLength > maxCharacters ? 'cpc--tag-truncate' : '';
  }
  var detachDescription = "".concat(t('Remove'), " ").concat(children);
  var onClose = function onClose() {
    trackComponentEvent('Clicked Remove Tag', {
      action: 'Remove Tag',
      'tag-label': children,
      palComponent: 'View - Tag'
    });
    setRemoved(true);
    if (onRemove) {
      onRemove(children);
    }
  };
  var handleKeyDown = function handleKeyDown(event) {
    if (event.key === 'Enter' || event.key === ' ') onClick(event);
  };
  var tagProps = {
    className: tagClasses,
    type: carbonType,
    tabIndex: -1,
    filter: isRemovable,
    onClick: onClick,
    onClose: onClose
  };
  if (onClick) {
    tagProps = _objectSpread2(_objectSpread2({}, tagProps), {}, {
      role: 'button',
      tabIndex: 0,
      onKeyDown: function onKeyDown(event) {
        return handleKeyDown(event);
      }
    });
  }
  var detachTitle = isRemovable ? detachDescription : null;
  var truncateTag = tagContentsClass === 'cpc--tag-truncate';

  // Use maxCharacter value for ch width unit. This is not 100% accurate but should be sufficient as ch is the width of the glyph "0" in the element font
  var inlineWidth = truncateTag ? {
    maxWidth: "".concat(maxCharacters, "ch")
  } : {};
  var tagTooltip = typeof children === 'string' ? children : title;
  return /*#__PURE__*/React.createElement(Tag$2, _extends({}, tagProps, other, {
    title: detachTitle
  }), /*#__PURE__*/React.createElement("span", {
    title: tagTooltip || null,
    style: truncateTag ? inlineWidth : null,
    className: truncateTag ? tagContentsClass : null
  }, children));
};
Tag.propTypes = {
  /**
   * The content to render in the tag.
   */
  children: PropTypes.node,
  /**
   * Any class names to be applied to carbon tag component.
   */
  className: PropTypes.string,
  /*
   * The color or the type of tag to render.
   */
  type: PropTypes.oneOf(Object.keys(TYPES)),
  /**
   * Whether or not the tag is removable or not.
   */
  isRemovable: PropTypes.bool,
  /**
   * The function to fire of upon removing the tag.
   */
  onRemove: PropTypes.func,
  /**
   * The function to fire off when click the tag.
   */
  onClick: PropTypes.func,
  /**
   * The maximum number of characters a tag can render. This is an approximation as it is applied using ch units.
   */
  maxCharacters: PropTypes.number,
  /**
   * The text to display as the tag tooltip, only used if typeof children !== 'string'.
   */
  title: PropTypes.string
};

// components should export a skeleton
Tag.skeleton = Tag$2.Skeleton;
var Tag$1 = Tag;

export { Tag$1 as default };
